<template>
    <div id="store-locator" class="use-background-color full-height">
        <div v-if="!storeChanging" class="columns">
            <div :class="style()">
                <div v-if="isStreamLined" class="has-text-centered pickup-header">
                </div>
                <h3 v-if="!isStreamLined"><b>Find Locations nearby</b></h3>
                <GmapAutocomplete v-if="getGmapAPIKey"
                    v-on:input.native="autoCompleteTxt = $event.target.value"
                    @place_changed="setPlace"
                    placeholder="Enter your address"
                    class="input text-input auto-complete-field"
                    type="text">
                </GmapAutocomplete>
				<input v-if="!getGmapAPIKey"
					id="store-name-field"
					type="text"
					class="input text-input auto-complete-field"
					placeholder="Enter Store Name or Address"
					@keyup="() => searchByName(storeLocations)"/>
                <div id="address-details" v-if="!loggedIn">
                    <div class="columns is-mobile is-gapless">
                        
                        <div class="column is-narrow is-relative">
                            <BaseIcon
                                class="input-check"
                                icon="checkCircleLight"
                                size="1x"
                                v-if="this.aptNum"/>
                            <input @change="setCurrentAddressObject" v-model="aptNum" class="input text-input" type="text" placeholder="Apt #">
                        </div>

                        <!-- notes -->
                        <div class="column is-relative">
                                <BaseIcon
                                    class="input-check"
                                    icon="checkCircleLight"
                                    size="1x"
                                    v-if="this.note"/>
                            <input @change="setCurrentAddressObject" v-model="note" class="input text-input" type="text" placeholder="Notes, Instructions, etc.">
                        </div>

                    </div>
                </div>
                <!-- current location of store -->
                <template v-if="location && location.id && location.onlineStoreOpen">
                    <h3><b>Your selected location</b></h3>

                    <location-card 
                        :name="location.name"
                        :address="location.address"
                        :phone="location.contact_phone"
                        :locationBase="location.id"
                        :location="location"
                        :showDetails="location.id === focusedLocation.id"
                        @emitActive="focusOnLocation(location)"
                        @emitHide="focusedLocation.id=null"
                        @setLocation="setOrderLocation(location)">
                    </location-card>
                </template>

                <!-- list of locations -->
                <div v-if="storeLocations.length">
                    <h3 v-if="userSearched"><b>Nearby Locations based on your address</b></h3>
                    <h3 v-else ><b>All locations</b></h3>
                    <location-card 
                        v-for="(pgLocation, index) in filteredLocations()"
                        :name="pgLocation.name"
                        :address="pgLocation.address"
                        :phone="pgLocation.contact_phone"
                        :key="index"
                        :locationBase="pgLocation.id"
                        :location="pgLocation"
                        :showDetails="pgLocation.id === focusedLocation.id"
                        @emitActive="focusOnLocation(pgLocation)"
                        @emitHide="focusedLocation.id=null"
                        @setLocation="setOrderLocation(pgLocation)"
                    >
                    </location-card>
                </div>
                <div v-else>
                    <h3 ><b>No nearby locations found</b></h3>
                    <BaseButton
                        :classNames="['is-rounded', 'store-button-color']"
                        @click="userSearched = false; 
                                storeLocations = locations;"
                        title="Show all Locations"
                    />
                </div>
            </div>

            <div v-if="!isStreamLined" class="column is-6 is-hidden-mobile">
                <div class="google-map">
					<BaseMap
						v-bind:locations="locations"
						v-bind:storeLocation="focusedLocation"
						width="100%"
						height="500px"
						:zoom="12" />
                </div>
            </div>

        </div>
        <div id="store-changing" class="has-text-centered" v-else>
            <p class="is-size-1">😊</p>
            <h3 class="is-size-3">You are currently being transferred to your new store</h3>
        </div>
         <v-dialog/>
    </div>
</template>

<script>
import { EServiceType } from '@/models/interfaces/Orders/EServiceType';
import { generateUniqueId, generateFullAddress } from '@/utils/helper.functions';
import { ICON_NAMES } from '@/components/common/BaseIcon.vue';
import { loaded } from 'vue2-google-maps';
import { LOCATION, ACCOUNT_CHECKOUT, FRANCHISOR, ACCOUNT, APP_STORE } from '@/stores/defs';
import { mapActions, mapGetters } from 'vuex';
import AddressModel from '@/models/AddressModel';
import addressService from '@/services/address.service';
import GoogleMarkerInfoWindow from '@/components/store-locator/GoogleMarkerInfoWindow';
import LocationCard from '@/components/store-locator/LocationCard';
import LocationModel from '@/models/LocationModel';
import locationService from '@/services/location.service';
import EventBus from "@EventBus";
import headerMixin from '@Mixin/headerMixin';
import mapsMixin from '@Mixin/mapsMixin';
import * as storeMarker from '@/assets/icons/maps/store-marker.png';
import { filter, isEmpty } from 'lodash';

export default {
    mixins: [
		headerMixin,
		mapsMixin,
	],
    props: {
        isStreamLined: {
            type: Boolean,
            default: false
        },
        hideModal: {
            type: Boolean,
            default: true
        }
    },
    components: {
        'location-card': LocationCard,
        'google-marker-info-window': GoogleMarkerInfoWindow
    },
    computed: {
        ...mapGetters({
            location: LOCATION.GET_LOCATION,
            serviceType: ACCOUNT_CHECKOUT.GET_SERVICE_TYPE,
            franchisor: FRANCHISOR.GET_FRANCHISOR,
            loggedIn: ACCOUNT.GET_LOGGED_IN,
            guestAddress: ACCOUNT.GET_GUEST_ADDRESS
        }),
        gMapApiKeyAvailable() {
            return this.location && this.location.deliveryFlowModel && this.location.deliveryFlowModel.googleMapsApiKey ||
                   (this.franchisor && this.franchisor.deliveryFlowModel && this.franchisor.deliveryFlowModel.googleMapsApiKey);
        },
        mapCenter() {
			let latLng = {}
            if (this.focusedLocation.latitude && this.focusedLocation.longitude) {
                return this.focusedLocation;
            } else if (this.location.latitude && this.location.longitude) {
                return {
                    latitude: this.location.latitude,
                    longitude: this.location.longitude
                }
            } else if (this.franchisor && this.franchisor.address) {
                this.getCenterByAddress(this.franchisor.address);
                return {};
            } else {
                return {};
            }
        }
    },
    mounted() {
		// get list from touchpoint API
		const vue = this;
        locationService
            .getLocations()
            .then(res => {
                // create map for better location access
                for (let pgLocation of res.data.body.sections.Nearby) {
                    pgLocation = new LocationModel(pgLocation);
                    vue.locationMap[`${pgLocation.latitude}::${pgLocation.longitude}`] = pgLocation;
                    vue.locations.push(pgLocation);
                }
                vue.storeLocations = vue.locations;
                vue.getUserCurrentLocation()
                .then((res) => {
					vue.focusedLocation = vue.currentAddress;
                    if (vue.currentAddress.latitude && vue.currentAddress.longitude) {
                        vue.findNearByLocations({latitude: vue.currentAddress.latitude, longitude: vue.currentAddress.longitude})
                    }  
                })
                .catch((err) => {
                    console.log(err);
                });

                // current location not in map
            })
            .catch(err => {
                console.log(err);
			});
		this._setHeaderTitle('Choose a location');
		this.$tpa.trackStoreLocatorPage();
    },
    data() {
        return {
            autoCompleteTxt: "",
            currentAddress: {},
            EServiceType: EServiceType,
			storeLocations: [],
			searchLocations: [],
            focusedLocation: {
                id: '',
                zoom: 11,
                latitude: null,
                longitude: null
            },
            ICON_NAMES: ICON_NAMES,
            locationMap: {},
            locations: [],
            markerIcon: storeMarker,
            nearbyLocations: [],
            storeChanging: false,
            userSearched: false,
            geocoder: null,
            aptNum: null, 
            note: null
        };
    },
    methods: {
        setCurrentAddressObject(latitude=null, longitude=null) {
            this.currentAddress = {
                ...this.currentAddress,
                ...(latitude && { latitude }),
                ...(longitude && { longitude }),
                ...(this.aptNum && {aptNum: this.aptNum}),
                ...(this.note && {aptNum: this.note})
            }
            if (this.guestAddress) {
                if (this.aptNum) {
                    this.guestAddress.aptNum = this.aptNum;
                }
                this.guestAddress.address = generateFullAddress(this.guestAddress.formattedAddress, this.aptNum);
                this.formattedAddress = this.guestAddress.formattedAddress;
                this.guestAddress.note = (this.note) ? this.note : null;
            }
        },
        getUserCurrentLocation() {
            return new Promise ((resolve, reject) => {
                if ('geolocation' in navigator) {
                    navigator.geolocation.getCurrentPosition(position => {
                        this.currentAddress.latitude = position.coords.latitude;
                        this.currentAddress.longitude = position.coords.longitude;
                        return resolve(true);
                    });
                } else {
                    return resolve(true);
                }
            })
        },
        ...mapActions({
            setLocation: LOCATION.SET_LOCATION,
            setGuestAddress: ACCOUNT.SET_GUEST_ADDRESS,
            setDebug: APP_STORE.SET_DEBUG,
            setServiceType: ACCOUNT_CHECKOUT.SET_SERVICE_TYPE
        }),
        focusOnLocation(location) {
            this.focusedLocation = {
                id: location.id,
                zoom: 13,
                latitude: location.latitude,
                longitude: location.longitude
            };
		},
		edgeCheckScrollTo(element) {
			if (typeof element.scrollTo === 'function') {
				return true;
			}
			return false;
		},
        setOrderLocation(location) {
			const modalContainer = this.$el.parentElement;
			if (this.edgeCheckScrollTo(window)) {
				window.scrollTo(0,0);
			}
			modalContainer.style.maxHeight = '300px';
			if (this.edgeCheckScrollTo(modalContainer)) {
				modalContainer.scrollTo(0,0);
			}
			this.storeChanging = true;
			this.setDebug(false);
			EventBus.$emit('store-changed', {
				locationId:location.id,
				hideInitialModal: this.hideModal
			});
			this.$tpa.trackStoreTransfer(location.id, location.name);
		},
		handleClickMapMarker(location) {
			this.focusedLocation = location;
		},
        setPlace(place) {
			if (place) {
				const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();

                this.setCurrentAddressObject(lat, lng);
                this.findNearByLocations(this.currentAddress);

                if (!this.loggedIn) {
                    this.setAddressForGuest(place);
				}

				const address = place.formatted_address;
				this.$tpa.trackLocatorSearch(address, lat, lng);
			}
        },
        setAddressForGuest(place) {
            let extractedGPlace = addressService.extractGPLaceProps(place);
            if (extractedGPlace) {
                const addressModel = new AddressModel({
                    id: generateUniqueId(),
                    ...extractedGPlace,
                    ...(this.aptNum && {aptNum: this.aptNum}),
                    ...(this.note && {aptNum: this.note})
                });
                

            this.setGuestAddress(addressModel);
            this.setServiceType(EServiceType.SERVICE_TYPE_DELIVERY);
            }
        },
        findNearByLocations(latLngObj) {
            this.userSearched = true;
            this.storeLocations = addressService.storeLocator(
                this.locations,
                { latitude: latLngObj.latitude, longitude: latLngObj.longitude },
                true
            );
        },
        getCenterByAddress(address) {
            loaded.then((res) => {
                if (google) {
                    if (!this.geocoder) {
                        this.geocoder = new google.maps.Geocoder();
                    }
                    this.geocoder.geocode({'address': address}, (results, status) => {
                        if (status === 'OK') {
                            this.focusedLocation.latitude = results[0].geometry.location.lat();
                            this.focusedLocation.longitude = results[0].geometry.location.lng();
                        } else {
                            console.log(status);
                        }
                    })
                }
            });
		},
		style() {
			const isStreamLined = this.isStreamLined;
			const displayMap = !!(this.getGmapAPIKey);
			return {
				'is-12' : isStreamLined || !displayMap,
				'is-6' : !isStreamLined,
				'is-12-mobile' : true,
				'column' : true,
			}
		},
		filteredLocations() {
			const hasSearch = !isEmpty(this.searchLocations);
			let useLocations = (hasSearch) ? this.searchLocations: this.storeLocations;
			return filter(useLocations, (storeLocation) => {
				return (storeLocation.id !== this.location.id);
			});
		},
		updateSearchLocations(locations) {
			this.searchLocations = locations;
		},
	},
    watch: {
        location: {
            handler(after, before) {
                // to set default address
                if (!this.focusedLocation.latitude && !this.focusedLocation.longitude && !this.focusedLocation.id) {
                    this.focusOnLocation(this.location);
                }
            },
            deep: true
        },
        autoCompleteTxt(newVal, oldVal) {
            if (!newVal.length) {
                this.getUserCurrentLocation()
                .then((res) => {
                    if (this.currentAddress.latitude && this.currentAddress.longitude) {
                        this.findNearByLocations({latitude: this.currentAddress.latitude, longitude: this.currentAddress.longitude})
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
            }
        }
	},
	created() {
		EventBus.$on('map-marker-clicked', this.handleClickMapMarker);
		EventBus.$on('updateSearchLocations', this.updateSearchLocations);
	},
	destroyed() {
		EventBus.$off('map-marker-clicked');
		EventBus.$off('updateSearchLocations');
	},
};
</script>

<style lang="scss">
@import 'globalStyles';

#store-changing {
    margin-top: 2em;
}
.google-map {
    top: 1em;
    position: -webkit-sticky;
    position: sticky;
}

.pickup-header {
    margin-bottom: 1em;
}

#store-locator {
    padding: 1em;
}

#address-details {
	margin-bottom: 2em;
	.column:last-of-type {
		margin-left: 0.5em;
	}
}

@include mobile {
    #store-locator {
		h3 {
			margin-bottom: 10px;
		}
	}
	.modal-main-content {
		#store-locator {
			padding-top: $mobileNavigationHeight;
		}
	}
	#store-locator-section {
		#store-locator {
			padding-top: 0;
		}
	}
}
</style>




// WEBPACK FOOTER //
// src/components/store-locator/StoreLocator/StoreLocator.vue